import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { 
  ArrowLeft, Search, Filter, Download, CreditCard, CheckCircle, 
  AlertCircle, Clock, XCircle, FileText, Calendar, Receipt,
  ChevronDown, RefreshCw, Eye, RotateCcw, Loader2, X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [paying, setPaying] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  const bid = activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchInvoices();
    }
  }, [bid]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/billing/invoices');
      setInvoices(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to load invoices');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      setPaying(invoiceId);
      await api.post(`/billing/invoices/${invoiceId}/pay`);
      toast.success('Invoice paid successfully!');
      fetchInvoices();
      setSelectedInvoice(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to pay invoice');
      console.error(error);
    } finally {
      setPaying(null);
    }
  };

  const handleDownloadPdf = (invoice) => {
    toast.success(`Downloading invoice ${invoice.invoice_no}...`);
    // Implement PDF download logic
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      PAID: { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-700', 
        icon: CheckCircle,
        label: 'Paid',
        dot: 'bg-emerald-500'
      },
      PENDING: { 
        bg: 'bg-amber-100', 
        text: 'text-amber-700', 
        icon: Clock,
        label: 'Due',
        dot: 'bg-amber-500'
      },
      DUE: { 
        bg: 'bg-amber-100', 
        text: 'text-amber-700', 
        icon: Clock,
        label: 'Due',
        dot: 'bg-amber-500'
      },
      OVERDUE: { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        icon: AlertCircle,
        label: 'Overdue',
        dot: 'bg-red-500'
      },
      FAILED: { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        icon: XCircle,
        label: 'Failed',
        dot: 'bg-red-500'
      },
      REFUNDED: { 
        bg: 'bg-purple-100', 
        text: 'text-purple-700', 
        icon: RotateCcw,
        label: 'Refunded',
        dot: 'bg-purple-500'
      }
    };
    return configs[status] || configs.PENDING;
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !searchTerm || 
      inv.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    total: invoices.length,
    paid: invoices.filter(i => i.status === 'PAID').length,
    pending: invoices.filter(i => ['PENDING', 'DUE'].includes(i.status)).length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.amount_due || 0), 0),
    paidAmount: invoices.filter(i => i.status === 'PAID').reduce((sum, i) => sum + (i.amount_due || 0), 0)
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-fadeIn">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM PAGE HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          {/* Title Row */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${bid}/billing`)}
                className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-[22px] font-bold text-slate-800 tracking-tight">Billing Invoices</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">View and manage all your subscription invoices</p>
              </div>
            </div>
            <button
              onClick={fetchInvoices}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all text-[13px] font-medium shadow-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Stats Pills */}
          <div className="flex items-center gap-3 flex-wrap mb-5">
            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200/60">
              <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center">
                <Receipt className="w-4 h-4 text-slate-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Total Invoices</p>
                <p className="text-[14px] font-bold text-slate-800">{stats.total}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200/60">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-medium">Paid</p>
                <p className="text-[14px] font-bold text-emerald-700">{stats.paid}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200/60">
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-amber-600 uppercase tracking-wider font-medium">Pending</p>
                <p className="text-[14px] font-bold text-amber-700">{stats.pending}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/60">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-blue-600 uppercase tracking-wider font-medium">Total Billed</p>
                <p className="text-[14px] font-bold text-blue-700">₹{stats.totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Search & Filter Row */}
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by invoice number..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
              />
            </div>

            {/* Status Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <Filter className="w-4 h-4 text-slate-400" />
                {statusFilter === 'ALL' ? 'All Status' : statusFilter}
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden animate-slideDown">
                  {['ALL', 'PAID', 'PENDING', 'OVERDUE', 'REFUNDED'].map(status => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-[13px] hover:bg-slate-50 transition-colors flex items-center justify-between ${
                        statusFilter === status ? 'bg-teal-50 text-teal-700' : 'text-slate-700'
                      }`}
                    >
                      {status === 'ALL' ? 'All Status' : status}
                      {statusFilter === status && <CheckCircle className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          
          {filteredInvoices.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-[16px] font-semibold text-slate-800 mb-2">No Invoices Found</h3>
              <p className="text-[13px] text-slate-500">
                {searchTerm || statusFilter !== 'ALL' 
                  ? 'Try adjusting your search or filter'
                  : 'Your invoices will appear here once generated'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                <div className="col-span-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Invoice</div>
                <div className="col-span-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Period</div>
                <div className="col-span-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Amount</div>
                <div className="col-span-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Status</div>
                <div className="col-span-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-right">Actions</div>
              </div>

              {/* Invoice Rows */}
              <div className="divide-y divide-slate-100">
                {filteredInvoices.map((invoice, index) => {
                  const statusConfig = getStatusConfig(invoice.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div
                      key={invoice.id}
                      className="grid grid-cols-12 gap-4 px-5 py-4 hover:bg-slate-50/50 transition-colors cursor-pointer animate-tableRowFadeIn"
                      style={{ animationDelay: `${index * 30}ms` }}
                      onClick={() => setSelectedInvoice(invoice)}
                    >
                      {/* Invoice Number */}
                      <div className="col-span-3 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${statusConfig.bg} flex items-center justify-center`}>
                          <StatusIcon className={`w-5 h-5 ${statusConfig.text}`} />
                        </div>
                        <div>
                          <p className="text-[14px] font-semibold text-slate-800">{invoice.invoice_no}</p>
                          <p className="text-[11px] text-slate-500">{formatDate(invoice.created_at)}</p>
                        </div>
                      </div>

                      {/* Period */}
                      <div className="col-span-3 flex items-center">
                        <div className="flex items-center gap-2 text-[13px] text-slate-600">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(invoice.period_start)} — {formatDate(invoice.period_end)}
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="col-span-2 flex items-center">
                        <div>
                          <p className="text-[15px] font-semibold text-slate-800">₹{invoice.amount_due?.toLocaleString() || 0}</p>
                          <p className="text-[11px] text-slate-500">{invoice.currency || 'INR'}</p>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2 flex items-center">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedInvoice(invoice);
                          }}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPdf(invoice);
                          }}
                          className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {['PENDING', 'DUE', 'FAILED'].includes(invoice.status) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePayInvoice(invoice.id);
                            }}
                            disabled={paying === invoice.id}
                            className="px-3 py-1.5 bg-teal-600 text-white text-[12px] font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                          >
                            {paying === invoice.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <CreditCard className="w-3.5 h-3.5" />
                            )}
                            Pay
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-[13px] text-slate-500">
              Showing <span className="font-medium text-slate-700">{filteredInvoices.length}</span> of <span className="font-medium text-slate-700">{invoices.length}</span> invoices
            </p>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          INVOICE DETAIL SLIDE-OVER
          ════════════════════════════════════════════════════════════════════════ */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-fadeIn"
            onClick={() => setSelectedInvoice(null)}
          />
          
          {/* Slide-over Panel */}
          <div className="relative w-full max-w-lg bg-white shadow-2xl animate-slideInRight">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-transparent">
              <div>
                <h2 className="text-[18px] font-bold text-slate-800">{selectedInvoice.invoice_no}</h2>
                <p className="text-[12px] text-slate-500">Invoice Details</p>
              </div>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto h-[calc(100vh-80px)]">
              {/* Status Card */}
              {(() => {
                const statusConfig = getStatusConfig(selectedInvoice.status);
                const StatusIcon = statusConfig.icon;
                return (
                  <div className={`${statusConfig.bg} rounded-xl p-4 mb-6 flex items-center gap-4`}>
                    <div className={`w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center`}>
                      <StatusIcon className={`w-6 h-6 ${statusConfig.text}`} />
                    </div>
                    <div>
                      <p className={`text-[14px] font-semibold ${statusConfig.text}`}>{statusConfig.label}</p>
                      <p className="text-[12px] text-slate-600">
                        {selectedInvoice.status === 'PAID' 
                          ? `Paid on ${formatDate(selectedInvoice.paid_at)}`
                          : `Due by ${formatDate(selectedInvoice.due_date)}`}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Invoice Info */}
              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <h3 className="text-[13px] font-semibold text-slate-700 mb-3">Invoice Information</h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between">
                    <span className="text-[12px] text-slate-500">Invoice Number</span>
                    <span className="text-[12px] font-medium text-slate-700">{selectedInvoice.invoice_no}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[12px] text-slate-500">Invoice Date</span>
                    <span className="text-[12px] font-medium text-slate-700">{formatDate(selectedInvoice.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[12px] text-slate-500">Due Date</span>
                    <span className="text-[12px] font-medium text-slate-700">{formatDate(selectedInvoice.due_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[12px] text-slate-500">Billing Period</span>
                    <span className="text-[12px] font-medium text-slate-700">
                      {formatDate(selectedInvoice.period_start)} — {formatDate(selectedInvoice.period_end)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="mb-6">
                <h3 className="text-[13px] font-semibold text-slate-700 mb-3">Line Items</h3>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {selectedInvoice.line_items?.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {selectedInvoice.line_items.map((item, idx) => (
                        <div key={idx} className="px-4 py-3 flex justify-between items-center">
                          <div>
                            <p className="text-[13px] font-medium text-slate-700">{item.description}</p>
                            {item.quantity && (
                              <p className="text-[11px] text-slate-500">Qty: {item.quantity}</p>
                            )}
                          </div>
                          <p className="text-[13px] font-semibold text-slate-800">₹{item.amount?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-3 flex justify-between items-center">
                      <p className="text-[13px] font-medium text-slate-700">Subscription Fee</p>
                      <p className="text-[13px] font-semibold text-slate-800">₹{selectedInvoice.amount_due?.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white mb-6">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-400">Subtotal</span>
                    <span>₹{(selectedInvoice.subtotal || selectedInvoice.amount_due)?.toLocaleString()}</span>
                  </div>
                  {selectedInvoice.tax_amount > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-400">Tax (18% GST)</span>
                      <span>₹{selectedInvoice.tax_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="text-slate-400">Discount</span>
                      <span className="text-emerald-400">-₹{selectedInvoice.discount_amount?.toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                  <span className="text-[14px] font-medium">Total Amount</span>
                  <span className="text-[22px] font-bold">₹{selectedInvoice.amount_due?.toLocaleString()}</span>
                </div>
              </div>

              {/* Payment Info (if paid) */}
              {selectedInvoice.status === 'PAID' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                  <h3 className="text-[13px] font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Payment Details
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[12px] text-emerald-700">Paid On</span>
                      <span className="text-[12px] font-medium text-emerald-800">{formatDate(selectedInvoice.paid_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[12px] text-emerald-700">Payment Method</span>
                      <span className="text-[12px] font-medium text-emerald-800">{selectedInvoice.payment_method || 'Card'}</span>
                    </div>
                    {selectedInvoice.transaction_id && (
                      <div className="flex justify-between">
                        <span className="text-[12px] text-emerald-700">Transaction ID</span>
                        <span className="text-[12px] font-medium text-emerald-800">{selectedInvoice.transaction_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={() => handleDownloadPdf(selectedInvoice)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-[14px] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>

                {['PENDING', 'DUE', 'FAILED'].includes(selectedInvoice.status) && (
                  <button
                    onClick={() => handlePayInvoice(selectedInvoice.id)}
                    disabled={paying === selectedInvoice.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl font-semibold text-[14px] transition-all shadow-lg shadow-teal-600/25 disabled:opacity-70"
                  >
                    {paying === selectedInvoice.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4" />
                        Pay Now
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes tableRowFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
        .animate-tableRowFadeIn {
          animation: tableRowFadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default InvoicesPage;
